import { useMemo, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Money } from '@rental/shared';
import { api } from '../lib/api.js';
import { AttachmentField } from '../components/Attachment.jsx';
import { Button, Card, Empty, Field, Page, Table, TextInput } from '../components/ui.jsx';

interface Asset {
  id: string;
  name: string;
  purchasedOn: string;
  installedOn: string | null;
  cost: number;
  vendor: string | null;
  warrantyUntil: string | null;
  propertyId: string | null;
}
interface Property { id: string; name: string; }
interface Category { slug: string; name: string; kind: 'opex' | 'installation' | 'other'; }

export function AssetsPage() {
  const qc = useQueryClient();
  const [showForm, setShowForm] = useState(false);

  const assetsQ = useQuery({
    queryKey: ['assets'],
    queryFn: () => api<{ assets: Asset[]; totalPaise: number }>('/api/v1/assets'),
  });
  const propertiesQ = useQuery({
    queryKey: ['properties'],
    queryFn: () => api<{ properties: Property[] }>('/api/v1/properties'),
  });
  const categoriesQ = useQuery({
    queryKey: ['expense-categories'],
    queryFn: () => api<{ categories: Category[] }>('/api/v1/expenses/categories'),
  });

  const propBy = useMemo(() => {
    const m = new Map<string, string>();
    for (const p of propertiesQ.data?.properties ?? []) m.set(p.id, p.name);
    return m;
  }, [propertiesQ.data]);

  const installationCats = (categoriesQ.data?.categories ?? []).filter(
    (c) => c.kind === 'installation',
  );

  return (
    <Page
      title="Assets"
      actions={<Button onClick={() => setShowForm((v) => !v)}>{showForm ? 'Cancel' : '+ New asset'}</Button>}
    >
      <Card className="mb-4">
        <div className="flex items-end justify-between">
          <p className="text-sm text-slate-500 max-w-lg">
            Assets track durable purchases — AC, geyser, furniture, CCTV, etc.
            Their cost also becomes an installation-category expense so
            monthly rollups don't need special-casing.
          </p>
          <div className="text-right">
            <div className="text-xs text-slate-500">Total invested</div>
            <div className="text-lg font-medium">
              {Money.fromPaise(assetsQ.data?.totalPaise ?? 0).format()}
            </div>
          </div>
        </div>
      </Card>

      {showForm && (
        <div className="mb-4">
          <AssetForm
            properties={propertiesQ.data?.properties ?? []}
            installationCategories={installationCats}
            onSaved={() => {
              setShowForm(false);
              void qc.invalidateQueries({ queryKey: ['assets'] });
              void qc.invalidateQueries({ queryKey: ['expenses'] });
            }}
          />
        </div>
      )}

      {assetsQ.isLoading ? (
        <Empty>Loading…</Empty>
      ) : !assetsQ.data?.assets.length ? (
        <Empty>No assets yet.</Empty>
      ) : (
        <Table>
          <thead className="text-left text-slate-500">
            <tr>
              <th className="border-b px-3 py-2 dark:border-slate-700">Name</th>
              <th className="border-b px-3 py-2 dark:border-slate-700">Purchased</th>
              <th className="border-b px-3 py-2 dark:border-slate-700">Installed</th>
              <th className="border-b px-3 py-2 dark:border-slate-700">Warranty</th>
              <th className="border-b px-3 py-2 dark:border-slate-700">Property</th>
              <th className="border-b px-3 py-2 dark:border-slate-700 text-right">Cost</th>
            </tr>
          </thead>
          <tbody>
            {assetsQ.data.assets.map((a) => (
              <tr key={a.id} className="border-b border-slate-100 dark:border-slate-800">
                <td className="px-3 py-2 font-medium">{a.name}</td>
                <td className="px-3 py-2 text-slate-500">{a.purchasedOn}</td>
                <td className="px-3 py-2 text-slate-500">{a.installedOn ?? '—'}</td>
                <td className="px-3 py-2 text-slate-500">{a.warrantyUntil ?? '—'}</td>
                <td className="px-3 py-2 text-slate-500">
                  {a.propertyId ? propBy.get(a.propertyId) ?? '—' : '—'}
                </td>
                <td className="px-3 py-2 text-right">{Money.fromPaise(a.cost).format()}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </Page>
  );
}

function AssetForm({
  properties, installationCategories, onSaved,
}: {
  properties: Property[];
  installationCategories: Category[];
  onSaved: () => void;
}) {
  const [name, setName] = useState('');
  const [purchasedOn, setPurchasedOn] = useState(new Date().toISOString().slice(0, 10));
  const [installedOn, setInstalledOn] = useState('');
  const [cost, setCost] = useState('');
  const [vendor, setVendor] = useState('');
  const [warrantyUntil, setWarrantyUntil] = useState('');
  const [expectedLife, setExpectedLife] = useState('');
  const [propertyId, setPropertyId] = useState('');
  const [categorySlug, setCategorySlug] = useState(
    installationCategories[0]?.slug ?? 'installation',
  );
  const [attachmentId, setAttachmentId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api('/api/v1/assets', { method: 'POST', body }),
    onSuccess: onSaved,
    onError: (e) => setError(e instanceof Error ? e.message : 'Save failed'),
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      const paise = Money.parse(cost).toPaise();
      mutation.mutate({
        name,
        purchasedOn,
        installedOn: installedOn || undefined,
        cost: paise,
        vendor: vendor || undefined,
        warrantyUntil: warrantyUntil || undefined,
        expectedLifeMonths: expectedLife ? Number(expectedLife) : undefined,
        propertyId: propertyId || undefined,
        categorySlug,
        attachmentId: attachmentId || undefined,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Invalid amount');
    }
  }

  return (
    <Card>
      <form onSubmit={onSubmit} className="grid gap-3 md:grid-cols-6 md:items-end">
        <div className="md:col-span-3">
          <Field label="Name">
            <TextInput required value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. 1.5T Split AC" />
          </Field>
        </div>
        <Field label="Cost (₹)">
          <TextInput required value={cost} onChange={(e) => setCost(e.target.value)} placeholder="35000" />
        </Field>
        <Field label="Purchased on">
          <TextInput type="date" required value={purchasedOn} onChange={(e) => setPurchasedOn(e.target.value)} />
        </Field>
        <Field label="Installed on">
          <TextInput type="date" value={installedOn} onChange={(e) => setInstalledOn(e.target.value)} />
        </Field>
        <Field label="Vendor">
          <TextInput value={vendor} onChange={(e) => setVendor(e.target.value)} />
        </Field>
        <Field label="Warranty until">
          <TextInput type="date" value={warrantyUntil} onChange={(e) => setWarrantyUntil(e.target.value)} />
        </Field>
        <Field label="Expected life (months)">
          <TextInput type="number" min={1} value={expectedLife} onChange={(e) => setExpectedLife(e.target.value)} />
        </Field>
        <Field label="Category">
          <select
            value={categorySlug} onChange={(e) => setCategorySlug(e.target.value)}
            className="w-full rounded border border-slate-300 bg-white px-3 py-2 dark:border-slate-600 dark:bg-slate-800"
          >
            {installationCategories.map((c) => (<option key={c.slug} value={c.slug}>{c.name}</option>))}
          </select>
        </Field>
        <Field label="Property">
          <select
            value={propertyId} onChange={(e) => setPropertyId(e.target.value)}
            className="w-full rounded border border-slate-300 bg-white px-3 py-2 dark:border-slate-600 dark:bg-slate-800"
          >
            <option value="">— none —</option>
            {properties.map((p) => (<option key={p.id} value={p.id}>{p.name}</option>))}
          </select>
        </Field>
        <div className="md:col-span-3 flex items-end">
          <AttachmentField entityType="asset" value={attachmentId} onChange={setAttachmentId} />
        </div>
        <div className="md:col-span-6 flex items-center justify-end gap-2">
          {error && <span className="text-xs text-red-600">{error}</span>}
          <Button type="submit" disabled={mutation.isPending}>Save asset</Button>
        </div>
      </form>
    </Card>
  );
}
